import { uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Loader } from '@components';
import Button from '@components/ButtonTs';
import InfiniteList from '@components/InfiniteList';
import Input from '@components/Input';
import Avatar from '@components/CRM/Clients/Avatar';
import { useDebounce } from '@hooks/index';

import ClientRelation from '../clients/ClientRelation';
import HotelName from '../components/HotelName';
import { B2BLeadStatus, B2BLeadCloseReason } from './types';
import { AGENT_LEADS_PATH, AGENT_LEADS_ROUTE } from './routes';
import { useClientsByEmail } from './useClientsByEmail';
import {
  fetchB2BLeadsAction, selectB2BLeadAction, takeB2BLeadAction,
  updateB2BLeadAction,
} from '../../../store/actions/b2bLeads';
import { clearActiveLead, setFilters } from '../../../store/reducers/b2bLeads';
import {
  activeB2BLeadIdSelector, activeB2BLeadSelector,
  b2bLeadsErrorSelector, b2bLeadsFiltersSelector,
  b2bLeadsHasMoreSelector, b2bLeadsNextBeforeIdSelector,
  b2bLeadsSelector, filteredB2BLeadsSelector,
  isB2BActiveLeadLoadingSelector, isB2BLeadUpdatingSelector,
  isB2BLeadsLoadingSelector,
} from '../../../store/selectors/b2bLeads';
import { agentSessionSelector } from '../../../store/selectors/agentAuth';
import {
  Block, BlockContent, BlockTitle, CheckboxLabel, CloseReasonSelect, CommentItem,
  ContactLine, Content, Detail, DetailColumn, DetailColumns, DetailGrid, DetailItem, DetailsHeader,
  DetailScroll, DetailsLoader, DetailsPanel, EmptyDetails, EmptyList, Filters,
  HeaderActions, HeaderMeta, LeadStatusSelect, ListBody, ListError, ListLoader, Muted, NextPageLoader, Page,
  LeadList, LeadRow, PrimaryButton, RelationsHint, RelationsLoader, RowMain, RowMeta, SearchInput, StatusBadge,
  StatusSelect, Title, Topbar,
} from './LeadsPage.styles';

export { AGENT_LEADS_PATH, AGENT_LEADS_ROUTE } from './routes';

const STATUS_OPTIONS = [
  { title: 'Все статусы', value: 'all' },
  { title: 'Новые', value: 'new' },
  { title: 'В работе', value: 'in_progress' },
  { title: 'Закрытые', value: 'closed' },
];
const STATUS_LABELS: Record<B2BLeadStatus, string> = {
  new: 'Новая', in_progress: 'В работе', closed: 'Закрыта',
};
// В выборе статуса и значение, и варианты — такие же бейджи, как статус в шапке.
const renderStatusBadge = (option?: { title: string; value: B2BLeadStatus }) =>
  option ? <StatusBadge $status={option.value}>{option.title}</StatusBadge> : null;
const CLOSE_REASON_OPTIONS = [
  { title: 'Заказ оформлен', value: 'ordered' },
  { title: 'Отказался', value: 'declined' },
  { title: 'Не удалось связаться', value: 'unreachable' },
  { title: 'Дубликат', value: 'duplicate' },
  { title: 'Спам', value: 'spam' },
];
const ACTION_STATUS_OPTIONS = [
  { title: 'Новая', value: 'new' },
  { title: 'В работе', value: 'in_progress' },
  { title: 'Закрыта', value: 'closed' },
];
const ROW_HEIGHT = 92;

const LeadsPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { lead_id: requestId } = useParams<{ lead_id?: string }>();
  const leads = useSelector(b2bLeadsSelector);
  const filteredLeads = useSelector(filteredB2BLeadsSelector);
  const selectedId = useSelector(activeB2BLeadIdSelector);
  const selected = useSelector(activeB2BLeadSelector);
  const filters = useSelector(b2bLeadsFiltersSelector);
  const nextBeforeId = useSelector(b2bLeadsNextBeforeIdSelector);
  const hasMore = useSelector(b2bLeadsHasMoreSelector);
  const error = useSelector(b2bLeadsErrorSelector);
  const isLoading = useSelector(isB2BLeadsLoadingSelector);
  const isLoadingActive = useSelector(isB2BActiveLeadLoadingSelector);
  const debouncedQuery = useDebounce(filters.query, 250);

  useEffect(() => {
    dispatch(fetchB2BLeadsAction({
      status: filters.status === 'all' ? undefined : filters.status,
      mine: filters.mine,
      perPage: 20,
      reset: true,
    }));
  }, [dispatch, filters.status, filters.mine]);

  useEffect(() => {
    if (!requestId) {
      if (selectedId || selected || isLoadingActive) dispatch(clearActiveLead());
      return;
    }
    if (selectedId !== requestId) dispatch(selectB2BLeadAction({ id: requestId }));
  }, [dispatch, isLoadingActive, requestId, selected, selectedId]);

  const handleLoadMore = () => {
    if (isLoading || !hasMore || !nextBeforeId) return;
    dispatch(fetchB2BLeadsAction({
      status: filters.status === 'all' ? undefined : filters.status,
      mine: filters.mine,
      beforeId: nextBeforeId,
      perPage: 20,
    }));
  };
  const handleSelect = (id: string) => {
    if (id !== selectedId) history.push(`${AGENT_LEADS_ROUTE}/${id}`);
  };

  return (
    <Page>
      <Topbar><div><Title>Заявки</Title><Muted>Обращения клиентов и запросы на подбор</Muted></div></Topbar>
      <Content>
        <LeadList>
          <Filters>
            <SearchInput aria-label="Поиск заявок" placeholder="Имя, телефон или email…" value={filters.query} onChange={(event) => dispatch(setFilters({ query: event.target.value }))} />
            <StatusSelect value={filters.status} options={STATUS_OPTIONS} onChange={(event: CustomEvent<{ value: B2BLeadStatus | 'all' }>) => dispatch(setFilters({ status: event.detail.value }))} />
            <CheckboxLabel><input type="checkbox" checked={filters.mine} onChange={(event) => dispatch(setFilters({ mine: event.target.checked }))} /> Только мои</CheckboxLabel>
          </Filters>
          <ListBody>
            {error && <ListError>{error}</ListError>}
            {isLoading && !leads.length ? <ListLoader /> : filteredLeads.length ? (
              <InfiniteList
                key={`${filters.status}-${filters.mine}-${debouncedQuery}`}
                isItemLoaded={(index) => index < filteredLeads.length}
                isNextPageLoading={isLoading}
                itemCount={hasMore ? filteredLeads.length + 1 : filteredLeads.length}
                itemData={filteredLeads}
                itemKey={(index, data) => data[index]?.id || `lead-loader-${index}`}
                itemSize={ROW_HEIGHT}
                onLoadRequest={handleLoadMore}
                threshold={3}
              >
                {({ data, index, style }) => {
                  const request = data[index];
                  if (!request) return <NextPageLoader style={style}>{isLoading && <Loader size={20} />}</NextPageLoader>;
                  return <LeadRow style={style} type="button" $active={request.id === selectedId} onClick={() => handleSelect(request.id)}>
                    <Avatar name={request.name || 'Заявка'} url={null} />
                    <RowMain><strong>{request.name || 'Без имени'}</strong><span>{request.phone || request.email || 'Контакты не указаны'}</span><small>{request.package?.hotel?.name || request.comment || 'Без описания'}</small></RowMain>
                    <RowMeta><StatusBadge $status={request.status}>{STATUS_LABELS[request.status]}</StatusBadge><small>{request.createdAt}</small></RowMeta>
                  </LeadRow>;
                }}
              </InfiniteList>
            ) : <EmptyList><strong>Заявки не найдены</strong><Muted>Попробуйте изменить фильтр</Muted></EmptyList>}
          </ListBody>
        </LeadList>
        <DetailsPanel>
          {isLoadingActive ? <DetailsLoader /> : selected ? <LeadDetails request={selected} /> : <EmptyDetails><strong>Выберите заявку</strong><Muted>Подробности заявки появятся здесь.</Muted></EmptyDetails>}
        </DetailsPanel>
      </Content>
    </Page>
  );
};

const LeadDetails: React.FC<{ request: any }> = ({ request }) => {
  const dispatch = useDispatch();
  const isUpdating = useSelector(isB2BLeadUpdatingSelector);
  const session = useSelector(agentSessionSelector);
  const [closeReason, setCloseReason] = useState<B2BLeadCloseReason | ''>(request.closeReason || '');
  const [nextStatus, setNextStatus] = useState<B2BLeadStatus>(request.status);
  useEffect(() => {
    setCloseReason(request.closeReason || '');
    setNextStatus(request.status);
  }, [request.id, request.closeReason, request.status]);
  // Статус меняет и сохраняет только тот, кто взял заявку в работу. Взять можно новую заявку,
  // а чужую в работе — только супервайзер (как проверяет бэкенд в take/update).
  const agencyUserId = session?.agency_user?.id;
  const isMine = Boolean(agencyUserId) && request.agencyUser?.id === agencyUserId;
  const isSupervisor = session?.role === 'supervisor';
  const canEditStatus = request.status === 'in_progress' && isMine;
  const canTake = Boolean(agencyUserId) && (request.status === 'new' || (request.status === 'in_progress' && !isMine && isSupervisor));
  const canReopen = request.status === 'closed' && Boolean(request.agencyUser) && (isMine || isSupervisor);
  // Клиент, оставивший заявку из личного кабинета, и клиенты агентства с той же почтой.
  const { clients: clientsByEmail, state: clientsSearchState } = useClientsByEmail(request.email);
  const relatedClients = uniqBy([request.client, ...clientsByEmail].filter(Boolean), 'id');
  const isSearchingClients = Boolean(request.email) && (clientsSearchState === 'idle' || clientsSearchState === 'loading');
  const updateStatus = (status: B2BLeadStatus) => dispatch(updateB2BLeadAction({ id: request.id, lead: { status, ...(status === 'closed' ? { close_reason: closeReason as B2BLeadCloseReason } : {}), ...(status === 'in_progress' && request.agencyUser ? { agency_user_id: request.agencyUser.id } : {}) } }));
  const close = () => { if (closeReason) updateStatus('closed'); };
  const saveStatus = () => {
    if (nextStatus === 'closed') close();
    else updateStatus(nextStatus);
  };
  return <Detail>
    <DetailsHeader>
      <div>
        <Muted>Заявка №{request.id}</Muted>
        <h2>{request.name || 'Новая заявка'}</h2>
        <ContactLine>{request.phone || request.email || 'Контакты не указаны'}</ContactLine>
      </div>
      <HeaderMeta>
        <HeaderActions>
          {canEditStatus ? (
            <>
              <LeadStatusSelect
                value={nextStatus}
                options={ACTION_STATUS_OPTIONS}
                headRenderer={renderStatusBadge}
                optionRenderer={renderStatusBadge}
                onChange={(event: CustomEvent<{ value: B2BLeadStatus }>) => setNextStatus(event.detail.value)}
              />
              {nextStatus === 'closed' && (
                <CloseReasonSelect
                  value={closeReason}
                  placeholder="Причина закрытия"
                  options={CLOSE_REASON_OPTIONS}
                  onChange={(event: CustomEvent<{ value: B2BLeadCloseReason }>) => setCloseReason(event.detail.value)}
                />
              )}
              <PrimaryButton
                variant="primary"
                size="tiny"
                disabled={isUpdating || nextStatus === request.status || (nextStatus === 'closed' && !closeReason)}
                onClick={saveStatus}
              >
                Сохранить
              </PrimaryButton>
            </>
          ) : (
            <StatusBadge $status={request.status}>{STATUS_LABELS[request.status]}</StatusBadge>
          )}
          {canTake && (
            <PrimaryButton variant="primary" size="tiny" disabled={isUpdating} onClick={() => dispatch(takeB2BLeadAction({ id: request.id }))}>
              Взять в работу
            </PrimaryButton>
          )}
          {canReopen && (
            <Button variant="default" size="tiny" disabled={isUpdating} onClick={() => updateStatus('in_progress')}>
              Открыть снова
            </Button>
          )}
        </HeaderActions>
        <small>{request.createdAt}</small>
      </HeaderMeta>
    </DetailsHeader>

    <DetailScroll>
      <DetailColumns>
        <DetailColumn>
          <Block>
            <BlockTitle>Информация о заявке</BlockTitle>
            <BlockContent>
              <DetailGrid>
                <DetailItem><span>Имя клиента</span><div>{request.name || '—'}</div></DetailItem>
                <DetailItem><span>Телефон</span><div>{request.phone || '—'}</div></DetailItem>
                <DetailItem><span>Email</span><div>{request.email || '—'}</div></DetailItem>
                <DetailItem><span>Источник</span><div>{request.source || '—'}</div></DetailItem>
                <DetailItem><span>Партнёр</span><div>{request.partner?.name || '—'}</div></DetailItem>
                <DetailItem><span>Ответственный</span><div>{request.agencyUser?.name || request.agencyUser?.email || 'Не назначен'}</div></DetailItem>
                {request.comment && <CommentItem><span>Комментарий</span><div>{request.comment}</div></CommentItem>}
              </DetailGrid>
            </BlockContent>
          </Block>

          {request.package && <Block>
            <BlockTitle>Параметры поездки</BlockTitle>
            <BlockContent><DetailGrid>
              <DetailItem><span>Отель</span><div>{request.package.hotel ? <HotelName hotelId={request.package.hotel.id} name={request.package.hotel.name} /> : '—'}</div></DetailItem>
              <DetailItem><span>Дата начала</span><div>{request.package.startDate}</div></DetailItem>
              <DetailItem><span>Продолжительность</span><div>{request.package.nights} ночей</div></DetailItem>
              <DetailItem><span>Туристы</span><div>{request.package.adults} взрослых, {request.package.kids} детей</div></DetailItem>
              <DetailItem><span>Тип номера</span><div>{request.package.room_type || '—'}</div></DetailItem>
            </DetailGrid></BlockContent>
          </Block>}

          {request.orders.length > 0 && <Block>
            <BlockTitle>Связанные заказы</BlockTitle>
            <BlockContent>{request.orders.map((order) => <div key={order.id}>№{order.id} · {order.state} · {order.createdAt}</div>)}</BlockContent>
          </Block>}
        </DetailColumn>

        <DetailColumn>
          <Block>
            <BlockTitle>Связанные сущности</BlockTitle>
            {relatedClients.length > 0
              ? relatedClients.map((client) => <ClientRelation key={client.id} client={client} />)
              : isSearchingClients
                ? <RelationsLoader />
                : <RelationsHint>{clientsSearchState === 'error' ? 'Не удалось найти клиента по почте' : 'Список пуст'}</RelationsHint>}
          </Block>
        </DetailColumn>
      </DetailColumns>
    </DetailScroll>
  </Detail>;
};

export default LeadsPage;
